import { Dispatch, SetStateAction } from 'react';
import { Action } from './action.interface';
import { Transcriber } from './transcriber.interface';
import { Voice } from './voice.interface';
import { Webhook } from './webhook.interface';

export type Language =
  | 'en'
  | 'es'
  | 'de'
  | 'hi'
  | 'pt'
  | 'fr'
  | 'nl'
  | 'id'
  | 'it'
  | 'ja'
  | 'ko'
  | 'multi';

export type InterruptSensitivity = 'low' | 'high';

export type EndpointSensitivity = 'auto' | 'relaxed' | 'sensitive';

export type LLMProvider = 'openai' | 'google' | 'azure';

export type Skill = {
  type: string;
  config: object;
};

export enum ActivityActionType {
  WRITE_ANALYSIS_ACTIVITY = 'write_analysis_activity',
}

export type ActivityActionConfig = {
  provider_type: string;
  destination_id: string;
};

export type ProviderConfig = {
  destination_id: string;
};

export enum GroupOperators {
  AND = 'AND',
  OR = 'OR',
}

export type GroupFilter = {
  groupOperator: GroupOperators;
  children: Array<BinaryExpression>;
};

export enum FieldOperators {
  EQ = 'EQ',
  NEQ = 'NEQ',
  GT = 'GT',
  GTE = 'GTE',
  LT = 'LT',
  LTE = 'LTE',
  CONTAINS = 'CONTAINS',
  NOT_CONTAINS = 'NOT_CONTAINS',
}

export type BinaryExpression = {
  left: string | number | boolean | null | undefined;
  operator: FieldOperators;
  right: string | number | boolean | null | undefined;
};

export enum PostCallFinalizedActionConditionType {
  BINARY_EXPRESSION = 'binary_expression',
  ANALYSIS_ITEM_BINARY_EXPRESSION = 'analysis_item_binary_expression',
}

export type PostCallAnalysisBinaryCondition = {
  type: PostCallFinalizedActionConditionType.ANALYSIS_ITEM_BINARY_EXPRESSION;
  analysisItemId: string;
  operator: FieldOperators;
  right: string | number | boolean | null | undefined;
};

export type PostCallFinalizedActionConditions = {
  groupOperator: GroupOperators;
  children: Array<BinaryExpression | PostCallAnalysisBinaryCondition>;
};

export enum PostCallActionType {
  EMAIL = 'email',
  SMS = 'sms',
  TRIGGER_CUSTOM_API = 'trigger_custom_api',
}

export enum PostCallAnalysisItemType {
  BOOLEAN = 'boolean',
  NUMBER = 'number',
  ENUM = 'enum',
  STRING = 'string',
}

export type PostCallAnalysisItem = {
  id: string;
  label?: string;
  instructions: string;
  type: PostCallAnalysisItemType;
  enumOptions?: string[];
};

export enum PostCallAnalysisResultItemStatus {
  EXTRACTED = 'extracted',
  COULD_NOT_DETERMINE = 'could_not_determine',
}

export type PostCallAnalysisResultItem = {
  label: string;
  value: boolean | number | string | null;
  rationale: string | null;
  confidence: number | null;
  status: PostCallAnalysisResultItemStatus;
};

export type NormalizedPostCallAnalysisResult = Record<
  string,
  PostCallAnalysisResultItem
>;

export type PostCallEmailConfig = {
  from_email: string;
  to_email: string;
  subject: string;
  body: string;
};

export type PostCallSmsConfig = {
  from_phone: string;
  to_phone: string;
  body: string;
};

export type PostCallCustomApiConfig = {
  http_method: string;
  relative_uri: string;
  body?: string;
  headers?: string;
};

export type PostCallFinalizedAction = {
  id: string;
  label?: string;
  type: PostCallActionType;
  provider_config: ProviderConfig;
  config: PostCallEmailConfig | PostCallSmsConfig | PostCallCustomApiConfig;
  conditions?: PostCallFinalizedActionConditions | null;
};

export enum BackgroundSound {
  KEYBOARD = 'keyboard',
  OFFICE = 'office',
  OFFICE_2 = 'office-2',
}

export type Agent = {
  id?: string;
  user_id?: string;
  name?: string;
  language: Language;
  initial_message?: string;
  interrupt_sensitivity?: InterruptSensitivity;
  max_idle_check_count?: number;
  idle_time_seconds?: number;
  call_duration_sec?: number;
  initial_message_delay?: number;
  endpointing_sensitivity?: EndpointSensitivity;
  conversation_speed?: number;
  ask_if_human_present_on_idle?: boolean;
  voice: string | Voice;
  model?: string;
  prompt:
    | string
    | {
        id?: string;
        content: string;
      };
  llm_fallback?: {
    provider: string;
    model_name: string;
  };
  llm_temperature?: number;
  enable_recording?: boolean;
  combine_last_call_context?: boolean;
  webhook: Webhook | string;
  actions: string[] | (Action & { originalId?: string })[];
  label?: string;
  description?: string;
  provider?: LLMProvider;
  transcriber?: Transcriber;
  voicemail_message?: string;
  skills?: Skill[];
  enable_mcp?: boolean;
  post_call_prompt?: string;
  post_call_analysis_items?: PostCallAnalysisItem[];
  post_call_actions?: PostCallFinalizedAction[];
  enable_memory?: boolean;
  memory_scope?: 'all' | 'single_agent' | null;
  activity_actions?: {
    type: ActivityActionType;
    config: ActivityActionConfig;
  }[];
  enable_dynamic_turns?: boolean;
  background_sound?: BackgroundSound;
  transfer_on_end?: boolean;
  enable_dynamic_vm_detection?: boolean;
};

export type AgentStepProps = {
  agent: Agent;
  setAgent: Dispatch<SetStateAction<Agent>>;
  loading?: boolean;
};

export const getReferencedContextParams = (agent: Agent): string[] => {
  const regex = /{([a-zA-Z_][a-zA-Z0-9_-]*)}/g;
  let match;

  const newContextObj: Set<string> = new Set();

  // Check prompt for variables
  if (agent.prompt) {
    while ((match = regex.exec(String(agent.prompt))) !== null) {
      newContextObj.add(match[1]);
    }
  }

  // Reset regex lastIndex
  regex.lastIndex = 0;

  // Check prompt content for variables if prompt is an object
  if (typeof agent.prompt === 'object' && agent.prompt?.content) {
    while ((match = regex.exec(String(agent.prompt.content))) !== null) {
      newContextObj.add(match[1]);
    }
  }

  // Reset regex lastIndex
  regex.lastIndex = 0;

  // Check initial message for variables
  if (agent.initial_message) {
    while ((match = regex.exec(String(agent.initial_message))) !== null) {
      newContextObj.add(match[1]);
    }
  }

  // Reset regex lastIndex
  regex.lastIndex = 0;

  // Check voicemail message for variables
  if (agent.voicemail_message) {
    while ((match = regex.exec(String(agent.voicemail_message))) !== null) {
      newContextObj.add(match[1]);
    }
  }

  return Array.from(newContextObj);
};
